import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };
type State = { error: Error | null; stack: string | null };

/** Surfaces render crashes instead of an empty blue screen (production swallows React errors). */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, stack: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, stack: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const stack = info.componentStack?.trim() ?? null;
  try {
      (window as unknown as { __BDA_CRASH__?: { message: string; stack: string | null } }).__BDA_CRASH__ =
        { message: error.message, stack };
    } catch {
      /* ignore */
    }
    console.error("[BDA] UI crash", error, stack);
    this.setState({ stack });
  }

  render() {
    const { error, stack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="auth-screen" data-layout="desktop">
        <div className="auth-card" role="alert">
          <div className="eyebrow">FEHLER</div>
          <h1>Spieloberfläche abgestürzt</h1>
          <p className="lede">
            Beim Laden der Leitstelle ist ein Fehler aufgetreten. Seite neu laden; wenn es bleibt,
            Spielstand zurücksetzen (Profil) oder Bug melden.
          </p>
          <pre
            className="muted small"
            style={{
              marginTop: 12,
              padding: 10,
              background: "rgba(0,0,0,0.25)",
              borderRadius: 8,
              overflow: "auto",
              maxHeight: 140,
              whiteSpace: "pre-wrap",
            }}
          >
            {error.message}
            {stack ? `\n\n${stack}` : ""}
          </pre>
          <div className="btn-row" style={{ marginTop: 16 }}>
            <button type="button" className="btn primary" onClick={() => location.reload()}>
              Neu laden
            </button>
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                try {
                  localStorage.removeItem("bda-auth");
                } catch {
                  /* ignore */
                }
                location.reload();
              }}
            >
              Abmelden
            </button>
          </div>
        </div>
      </div>
    );
  }
}
